import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as constants from '../../constants/CONSTANT';
import axios from 'axios';
import DataApi from '../../api/DataApi';
import AppToast from '../AppToast';
import MultiSelectDropdown from '../layout/MultiSelectDropdown1';
import './css/PermissionBulkEdit.css';

const ADMIN_PERM_FLOOR_KEYS = [
  'can_create',
  'can_read',
  'can_update',
  'can_delete',
  'view_all',
  'modify_all',
];

function applyAdminPermissionFloor(permission, original, isEditingAdminRole) {
  if (!isEditingAdminRole || !original) return permission;
  const next = { ...permission };
  for (const k of ADMIN_PERM_FLOOR_KEYS) {
    if (original[k] === true) next[k] = true;
  }
  return next;
}

const PermissionBulkEdit = () => {
  const { id: roleId } = useParams();
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [originalPermissions, setOriginalPermissions] = useState([]);
  const [role, setRole] = useState(null);
  const [licenseCeiling, setLicenseCeiling] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [allowedStandardTabs, setAllowedStandardTabs] = useState([]);
  const [allowedCustomTabs, setAllowedCustomTabs] = useState([]);
  const [roleTabNames, setRoleTabNames] = useState([]);
  const [originalRoleTabNames, setOriginalRoleTabNames] = useState([]);
   const [showAiChatbot, setShowAiChatbot] = useState(true);
  const [savingAiChatbot, setSavingAiChatbot] = useState(false);
  const roleApi = new DataApi('roles');
  const permissionApi = new DataApi('permissions/bulk');

  useEffect(() => {
    if (roleId) {
      fetchRole();
      fetchPermissions();
    }
  }, [roleId]);

  const fetchRole = async () => {
    try {
      const response = await roleApi.fetchById(roleId);
      if (response.data.success) {
        setRole(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching role:', err);
      setError('Failed to fetch role information');
    }
  };

  const fetchPermissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${constants.API_BASE_URL}/api/permissions/role/${roleId}`, {
        headers: {
          Authorization: token,
        },
      });
      if (response.data.success) {
        const permissionsData = response.data.data;
        setPermissions(permissionsData);
        setOriginalPermissions(JSON.parse(JSON.stringify(permissionsData)));
        setLicenseCeiling(response.data.licenseCeiling && typeof response.data.licenseCeiling === 'object' ? response.data.licenseCeiling : {});
        setAllowedStandardTabs(Array.isArray(response.data.allowedStandardTabs) ? response.data.allowedStandardTabs : []);
        setAllowedCustomTabs(Array.isArray(response.data.allowedCustomTabs) ? response.data.allowedCustomTabs : []);
        const names = Array.isArray(response.data.selectedTabNames) ? response.data.selectedTabNames : (Array.isArray(response.data.roleTabNames) ? response.data.roleTabNames : []);
        setRoleTabNames(names);
        setOriginalRoleTabNames(names);
        setShowAiChatbot(response.data.showAiChatbot !== false);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch permissions');
      console.error('Error fetching permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  /** Whether the role's company_license allows granting this permission for this object. */
  const isAllowedByLicense = (objectName, permissionType) => {
    const objCeiling = licenseCeiling && objectName ? licenseCeiling[objectName] : null;
    if (!objCeiling || typeof objCeiling !== 'object') return true;
    return objCeiling[permissionType] !== false;
  };

  const handlePermissionChange = (objectName, permissionType, value) => {
    if (value === true && !isAllowedByLicense(objectName, permissionType)) return;
    const isEditingAdminRole =
      String(role?.fields?.find((f) => f.field_name === 'name')?.value || role?.name || '').toUpperCase() === 'ADMIN';

    setPermissions(prevPermissions =>
      prevPermissions.map(permission => {
        if (permission.object_name === objectName) {
          const original = originalPermissions.find(
            (p) => p.object_name === permission.object_name && p.role_id === permission.role_id
          );
          if (isEditingAdminRole && original && original[permissionType] === true && value === false) {
            return permission;
          }

          let updatedPermission = { ...permission, [permissionType]: value };

          // Implement comprehensive permission dependencies
          if (permissionType === 'view_all' && value === true) {
            // If view all is checked, automatically check read
            updatedPermission.can_read = true;
          } else if (permissionType === 'modify_all' && value === true) {
            // If modify all is checked, automatically check all permissions
            updatedPermission.can_read = true;
            updatedPermission.can_create = true;
            updatedPermission.can_update = true;
            updatedPermission.can_delete = true;
            updatedPermission.view_all = true;
          } else if (permissionType === 'can_read' && value === false) {
            // If read is unchecked, uncheck all dependent permissions
            updatedPermission.can_create = false;
            updatedPermission.can_update = false;
            updatedPermission.can_delete = false;
            updatedPermission.view_all = false;
            updatedPermission.modify_all = false;
          } else if (permissionType === 'can_create' && value === false) {
            // If create is unchecked, uncheck modify all
            updatedPermission.modify_all = false;
          } else if (permissionType === 'can_update' && value === false) {
            // If update is unchecked, uncheck modify all
            updatedPermission.modify_all = false;
          } else if (permissionType === 'can_delete' && value === false) {
            // If delete is unchecked, uncheck modify all
            updatedPermission.modify_all = false;
          } else if (permissionType === 'view_all' && value === false) {
            // If view all is unchecked, no other permissions are affected
            // (view all is independent when unchecked)
          } else if (permissionType === 'modify_all' && value === false) {
            // If modify all is unchecked, no other permissions are affected
            // (modify all is independent when unchecked)
          } else if (permissionType === 'can_create' && value === true) {
            // If create is checked, automatically check read
            updatedPermission.can_read = true;
          } else if (permissionType === 'can_update' && value === true) {
            // If update is checked, automatically check read
            updatedPermission.can_read = true;
          } else if (permissionType === 'can_delete' && value === true) {
            // If delete is checked, automatically check read
            updatedPermission.can_read = true;
          }

          return applyAdminPermissionFloor(updatedPermission, original, isEditingAdminRole);
        }
        return permission;
      })
    );
  };

  // Function to detect changed permissions
  const getChangedPermissions = () => {
    const changedPermissions = [];

    permissions.forEach((permission, index) => {
      const original = originalPermissions[index];

      // Check if this is a new permission (not existing in original)
      if (!permission.is_existing) {
        // Only include if any permission is actually set to true
        if (permission.can_create || permission.can_read || permission.can_update || permission.can_delete || permission.view_all || permission.modify_all) {
          changedPermissions.push({
            // Don't include id field for new permissions - let database generate UUID
            role_id: permission.role_id,
            object_name: permission.object_name,
            can_create: permission.can_create,
            can_read: permission.can_read,
            can_update: permission.can_update,
            can_delete: permission.can_delete,
            view_all: permission.view_all,
            modify_all: permission.modify_all,
            is_active: permission.is_active
          });
        }
        return;
      }

      // For existing permissions, compare with original
      if (original) {
        const hasChanged =
          permission.can_create !== original.can_create ||
          permission.can_read !== original.can_read ||
          permission.can_update !== original.can_update ||
          permission.can_delete !== original.can_delete ||
          permission.view_all !== original.view_all ||
          permission.modify_all !== original.modify_all ||
          permission.is_active !== original.is_active;

        if (hasChanged) {
          changedPermissions.push({
            id: permission.id,
            role_id: permission.role_id,
            object_name: permission.object_name,
            can_create: permission.can_create,
            can_read: permission.can_read,
            can_update: permission.can_update,
            can_delete: permission.can_delete,
            view_all: permission.view_all,
            modify_all: permission.modify_all,
            is_active: permission.is_active
          });
        }
      }
    });

    return changedPermissions;
  };

  const tabsChanged = () => {
    if (roleTabNames.length !== originalRoleTabNames.length) return true;
    const setA = new Set(roleTabNames.map((n) => String(n).toLowerCase()));
    const setB = new Set(originalRoleTabNames.map((n) => String(n).toLowerCase()));
    if (setA.size !== setB.size) return true;
    for (const n of setA) if (!setB.has(n)) return true;
    return false;
  };

  /** Tab names allowed (standard from license + custom). We send only these. */
  const allowedTabNameSet = new Set(
    [...allowedStandardTabs, ...allowedCustomTabs].map((t) => String(t.name || t.label || t.id).trim())
  );
  const tabNamesToSend = roleTabNames.filter(
    (n) => n != null && String(n).trim() !== '' && allowedTabNameSet.has(String(n).trim())
  );

  const handleSaveAll = async () => {
    setSaving(true);
    setError(null);

    try {
      const changedPermissions = getChangedPermissions();
      const tabsNeedSave = tabsChanged();

      if (changedPermissions.length === 0 && !tabsNeedSave) {
        setError('No changes detected. Nothing to save.');
        setSaving(false);
        return;
      }

      const messages = [];

      if (changedPermissions.length > 0) {
        const response = await permissionApi.upsert({ permissions: changedPermissions });
        if (response.data.success) {
          const { summary } = response.data.data;
          let msg = `Permissions: ${summary.created || 0} created, ${summary.updated || 0} updated`;
          if (summary.failed > 0) msg += `, ${summary.failed} failed`;
          messages.push(msg);
        } else {
          setError('Failed to save permissions');
          setSaving(false);
          return;
        }
      }

      if (tabsNeedSave) {
        const token = localStorage.getItem('token');
        const tabRes = await axios.put(
          `${constants.API_BASE_URL}/api/permissions/role/${roleId}/tabs`,
          { tabNames: tabNamesToSend },
          { headers: { Authorization: token } }
        );
        if (tabRes.data?.success) {
          messages.push('Tabs updated');
          setOriginalRoleTabNames(tabNamesToSend);
        } else {
          setError((prev) => (prev ? prev + '; tabs update failed.' : 'Failed to save tabs'));
          setSaving(false);
          return;
        }
      }

      const message = messages.join('. ');
      navigate(`/setup/permission/${roleId}?success=true&message=${encodeURIComponent(message)}`);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to save';
      setError(errorMessage);
      console.error('Error saving:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToastClose = () => {
    setShowToast(false);
  };

  const handleCancel = () => {
    navigate(`/setup/permission/${roleId}`);
  };

  const handleSelectAll = (permissionType, value) => {
    const isEditingAdminRole =
      String(role?.fields?.find((f) => f.field_name === 'name')?.value || role?.name || '').toUpperCase() === 'ADMIN';

    setPermissions(prevPermissions =>
      prevPermissions.map(permission => {
        const original = originalPermissions.find(
          (p) => p.object_name === permission.object_name && p.role_id === permission.role_id
        );
        const allowed = isAllowedByLicense(permission.object_name, permissionType);
        const effectiveValue = value === true && !allowed ? permission[permissionType] : value;
        let updatedPermission = { ...permission, [permissionType]: effectiveValue };

        // Implement comprehensive permission dependencies for bulk operations
        if (permissionType === 'view_all' && value === true) {
          // If view all is checked, automatically check read
          updatedPermission.can_read = true;
        } else if (permissionType === 'modify_all' && value === true) {
          // If modify all is checked, automatically check all permissions
          updatedPermission.can_read = true;
          updatedPermission.can_create = true;
          updatedPermission.can_update = true;
          updatedPermission.can_delete = true;
          updatedPermission.view_all = true;
        } else if (permissionType === 'can_read' && value === false) {
          // If read is unchecked, uncheck all dependent permissions
          updatedPermission.can_create = false;
          updatedPermission.can_update = false;
          updatedPermission.can_delete = false;
          updatedPermission.view_all = false;
          updatedPermission.modify_all = false;
        } else if (permissionType === 'can_create' && value === false) {
          // If create is unchecked, uncheck modify all
          updatedPermission.modify_all = false;
        } else if (permissionType === 'can_update' && value === false) {
          // If update is unchecked, uncheck modify all
          updatedPermission.modify_all = false;
        } else if (permissionType === 'can_delete' && value === false) {
          // If delete is unchecked, uncheck modify all
          updatedPermission.modify_all = false;
        } else if (permissionType === 'view_all' && value === false) {
          // If view all is unchecked, no other permissions are affected
          // (view all is independent when unchecked)
        } else if (permissionType === 'modify_all' && value === false) {
          // If modify all is unchecked, no other permissions are affected
          // (modify all is independent when unchecked)
        } else if (permissionType === 'can_create' && value === true) {
          // If create is checked, automatically check read
          updatedPermission.can_read = true;
        } else if (permissionType === 'can_update' && value === true) {
          // If update is checked, automatically check read
          updatedPermission.can_read = true;
        } else if (permissionType === 'can_delete' && value === true) {
          // If delete is checked, automatically check read
          updatedPermission.can_read = true;
        }

        return applyAdminPermissionFloor(updatedPermission, original, isEditingAdminRole);
      })
    );
  };

  const handleSelectAllPermissions = (value) => {
    const isEditingAdminRole =
      String(role?.fields?.find((f) => f.field_name === 'name')?.value || role?.name || '').toUpperCase() === 'ADMIN';

    setPermissions(prevPermissions =>
      prevPermissions.map(permission => {
        const original = originalPermissions.find(
          (p) => p.object_name === permission.object_name && p.role_id === permission.role_id
        );
        const next = {
          ...permission,
          can_create: value,
          can_read: value,
          can_update: value,
          can_delete: value,
          view_all: value,
          modify_all: value,
        };
        return applyAdminPermissionFloor(next, original, isEditingAdminRole);
      })
    );
  };

  const handleSelectAllForObject = (objectName, value) => {
    const isEditingAdminRole =
      String(role?.fields?.find((f) => f.field_name === 'name')?.value || role?.name || '').toUpperCase() === 'ADMIN';

    setPermissions(prevPermissions =>
      prevPermissions.map(permission => {
        if (permission.object_name === objectName) {
          const original = originalPermissions.find(
            (p) => p.object_name === permission.object_name && p.role_id === permission.role_id
          );
          const next = {
            ...permission,
            can_create: value,
            can_read: value,
            can_update: value,
            can_delete: value,
            view_all: value,
            modify_all: value,
          };
          return applyAdminPermissionFloor(next, original, isEditingAdminRole);
        }
        return permission;
      })
    );
  };

  const areAllPermissionsSelectedForObject = (permission) => {
    return permission.can_create === true &&
      permission.can_read === true &&
      permission.can_update === true &&
      permission.can_delete === true &&
      permission.view_all === true &&
      permission.modify_all === true;
  };

  // Check if all permissions of a specific type are selected
  const areAllSelected = (permissionType) => {
    if (filteredPermissions.length === 0) return false;
    return filteredPermissions.every(permission => permission[permissionType] === true);
  };

  // Check if some permissions of a specific type are selected (for indeterminate state)
  const areSomeSelected = (permissionType) => {
    if (filteredPermissions.length === 0) return false;
    const selectedCount = filteredPermissions.filter(permission => permission[permissionType] === true).length;
    return selectedCount > 0 && selectedCount < filteredPermissions.length;
  };

  // Check if all permissions are selected for all types
  const areAllPermissionsSelected = () => {
    if (filteredPermissions.length === 0) return false;
    return filteredPermissions.every(permission =>
      permission.can_create === true &&
      permission.can_read === true &&
      permission.can_update === true &&
      permission.can_delete === true &&
      permission.view_all === true &&
      permission.modify_all === true
    );
  };

  // Check if some permissions are selected (for indeterminate state of All checkbox)
  const areSomePermissionsSelected = () => {
    if (filteredPermissions.length === 0) return false;
    const allSelectedCount = filteredPermissions.filter(permission =>
      permission.can_create === true &&
      permission.can_read === true &&
      permission.can_update === true &&
      permission.can_delete === true &&
      permission.view_all === true &&
      permission.modify_all === true
    ).length;
    return allSelectedCount > 0 && allSelectedCount < filteredPermissions.length;
  };

  // Check if a permission has been changed from its original state
  const hasPermissionChanged = (permission) => {
    const original = originalPermissions.find(p =>
      p.object_name === permission.object_name && p.role_id === permission.role_id
    );

    if (!original) {
      // New permission - only changed if any permission is set to true
      return permission.can_create || permission.can_read || permission.can_update || permission.can_delete || permission.view_all || permission.modify_all;
    }

    return (
      permission.can_create !== original.can_create ||
      permission.can_read !== original.can_read ||
      permission.can_update !== original.can_update ||
      permission.can_delete !== original.can_delete ||
      permission.view_all !== original.view_all ||
      permission.modify_all !== original.modify_all ||
      permission.is_active !== original.is_active
    );
  };

  const filteredPermissions = permissions.filter(permission =>
    permission.object_display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    permission.object_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const standardPermissions = filteredPermissions.filter((p) => p.is_standard === true);
  const customPermissions = filteredPermissions.filter((p) => p.is_standard !== true);

  const roleNameForEdit =
    role?.fields?.find((f) => f.field_name === 'name')?.value || role?.name || '';
  const isEditingAdminRole = String(roleNameForEdit).toUpperCase() === 'ADMIN';

  const getOriginalPermission = (permission) =>
    originalPermissions.find(
      (p) => p.object_name === permission.object_name && p.role_id === permission.role_id
    );

  const permCheckboxProps = (permission, key) => {
    const orig = getOriginalPermission(permission);
    const licOk = isAllowedByLicense(permission.object_name, key);
    const adminLock = isEditingAdminRole && orig && orig[key] === true;
    let title = '';
    if (!licOk) title = 'Not allowed by license';
    else if (adminLock) title = 'Required for Admin role (cannot remove)';
    return { disabled: !licOk || adminLock, title };
  };

  const tabKeyMatching = (name, tabList) => {
    const t = tabList.find(
      (tab) =>
        String(name).toLowerCase() === String(tab.name || '').toLowerCase() ||
        String(name).toLowerCase() === String(tab.label || '').toLowerCase()
    );
    return t ? String(t.name || t.label || t.id) : null;
  };
  const lockedStandardTabKeys = isEditingAdminRole
    ? originalRoleTabNames.map((n) => tabKeyMatching(n, allowedStandardTabs)).filter(Boolean)
    : [];
  const lockedCustomTabKeys = isEditingAdminRole
    ? originalRoleTabNames.map((n) => tabKeyMatching(n, allowedCustomTabs)).filter(Boolean)
    : [];

  const renderBulkEditPermissionRow = (permission) => {
    const pc = (k) => permCheckboxProps(permission, k);
    const pcc = pc('can_create');
    const pcr = pc('can_read');
    const pcu = pc('can_update');
    const pcd = pc('can_delete');
    const pcv = pc('view_all');
    const pcm = pc('modify_all');
    return (
      <tr
        key={permission.id || permission.object_name}
        className={hasPermissionChanged(permission) ? 'changed-permission' : ''}
      >
        <td className="object-cell">
          <div className="object-name">
            {permission.object_display_name || permission.object_name}
          </div>
        </td>
        <td className="checkbox-cell">
          <input
            type="checkbox"
            checked={permission.can_create || false}
            onChange={(e) => handlePermissionChange(permission.object_name, 'can_create', e.target.checked)}
            disabled={pcc.disabled}
            className="permission-checkbox"
            title={pcc.title || undefined}
          />
        </td>
        <td className="checkbox-cell">
          <input
            type="checkbox"
            checked={permission.can_read || false}
            onChange={(e) => handlePermissionChange(permission.object_name, 'can_read', e.target.checked)}
            disabled={pcr.disabled}
            className="permission-checkbox"
            title={pcr.title || undefined}
          />
        </td>
        <td className="checkbox-cell">
          <input
            type="checkbox"
            checked={permission.can_update || false}
            onChange={(e) => handlePermissionChange(permission.object_name, 'can_update', e.target.checked)}
            disabled={pcu.disabled}
            className="permission-checkbox"
            title={pcu.title || undefined}
          />
        </td>
        <td className="checkbox-cell">
          <input
            type="checkbox"
            checked={permission.can_delete || false}
            onChange={(e) => handlePermissionChange(permission.object_name, 'can_delete', e.target.checked)}
            disabled={pcd.disabled}
            className="permission-checkbox"
            title={pcd.title || undefined}
          />
        </td>
        <td className="checkbox-cell">
          <input
            type="checkbox"
            checked={permission.view_all || false}
            onChange={(e) => handlePermissionChange(permission.object_name, 'view_all', e.target.checked)}
            disabled={pcv.disabled}
            className="permission-checkbox"
            title={pcv.title || undefined}
          />
        </td>
        <td className="checkbox-cell">
          <input
            type="checkbox"
            checked={permission.modify_all || false}
            onChange={(e) => handlePermissionChange(permission.object_name, 'modify_all', e.target.checked)}
            disabled={pcm.disabled}
            className="permission-checkbox"
            title={pcm.title || undefined}
          />
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <div className="permission-bulk-edit-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="permission-bulk-edit-page">
      {/* Header */}
      <div className="bulk-edit-header">
        <div className="header-left">
          <button className="btn-back" onClick={handleCancel}>
            <i className="fas fa-arrow-left"></i>
            Back
          </button>
          <div className="header-info">
            <h1>Edit Permissions</h1>
            <p>Role: {role?.fields?.find(f => f.field_name === 'name')?.value || role?.name || 'Loading...'}</p>
            {isEditingAdminRole && (
              <p className="text-muted small mb-0 mt-1">
                Admin role: permissions and tabs that were already granted stay checked and cannot be removed; you can
                still add more.
              </p>
            )}
          </div>
        </div>
        <div className="header-actions">
          <button
            className="btn-save"
            onClick={handleSaveAll}
            disabled={saving}
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Save
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-alert">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}

      {!loading && permissions.length > 0 && (
        <div className="bulk-edit-content">
          {/* Role options: AI Chatbot visibility (subscription/license must allow; role can further restrict) */}
          <div className="permissions-role-options-section bulk-edit-tabs mb-3">
            <h3>Role options</h3>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="showAiChatbot"
                checked={showAiChatbot}
                disabled={savingAiChatbot}
                onChange={async (e) => {
                  const value = e.target.checked;
                  setShowAiChatbot(value);
                  setSavingAiChatbot(true);
                  try {
                    const token = localStorage.getItem('token');
                    await axios.put(
                      `${constants.API_BASE_URL}/api/permissions/role/${roleId}/ai-chatbot`,
                      { showAiChatbot: value },
                      { headers: { Authorization: token } }
                    );
                    setToastMessage(value ? 'AI Chatbot enabled for this role.' : 'AI Chatbot disabled for this role.');
                    setToastVariant('success');
                    setShowToast(true);
                  } catch (err) {
                    setShowAiChatbot(!value);
                    setToastMessage(err.response?.data?.message || 'Failed to update AI Chatbot permission');
                    setToastVariant('danger');
                    setShowToast(true);
                  } finally {
                    setSavingAiChatbot(false);
                  }
                }}
              />
              <label className="form-check-label" htmlFor="showAiChatbot">
                Show AI Chatbot (only applies when plan and license allow it)
              </label>
              {savingAiChatbot && <span className="ms-2 text-muted small">Saving...</span>}
            </div>
          </div>

          {/* Tabs Section - app-tabs: standard (restricted by license) + custom; selected stored in role.permission_config */}
          <div className="permissions-tabs-section bulk-edit-tabs">
            <h3>Tabs</h3>
            <p className="permissions-tabs-summary text-muted small mb-2">
              Assign tabs this role can see. Standard tabs are limited by the role&apos;s license (e.g. Account may not be assignable on limited licenses).
            </p>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Standard Tabs</label>
                <MultiSelectDropdown
                  picklistValues={allowedStandardTabs.map((t) => ({
                    key: t.name || t.label || String(t.id),
                    value: (t.label || t.name || t.id) + (t.object_name ? ` (${t.object_name})` : ''),
                  }))}
                  selectedValues={roleTabNames
                    .map((n) => {
                      const t = allowedStandardTabs.find(
                        (tab) =>
                          String(n).toLowerCase() === String(tab.name || '').toLowerCase() ||
                          String(n).toLowerCase() === String(tab.label || '').toLowerCase()
                      );
                      return t ? (t.name || t.label || String(t.id)) : null;
                    })
                    .filter(Boolean)}
                  onChange={(selectedKeys) => {
                    const customKeys = roleTabNames.filter((name) =>
                      allowedCustomTabs.some(
                        (t) =>
                          String(name).toLowerCase() === String(t.name || '').toLowerCase() ||
                          String(name).toLowerCase() === String(t.label || '').toLowerCase()
                      )
                    );
                    setRoleTabNames([...customKeys, ...selectedKeys]);
                  }}
                  lockedKeys={lockedStandardTabKeys}
                  placeholder="Select standard tabs..."
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Custom Tabs</label>
                <MultiSelectDropdown
                  picklistValues={allowedCustomTabs.map((t) => ({
                    key: t.name || t.label || String(t.id),
                    value: (t.label || t.name || t.id) + (t.object_name ? ` (${t.object_name})` : ''),
                  }))}
                  selectedValues={roleTabNames
                    .map((n) => {
                      const t = allowedCustomTabs.find(
                        (tab) =>
                          String(n).toLowerCase() === String(tab.name || '').toLowerCase() ||
                          String(n).toLowerCase() === String(tab.label || '').toLowerCase()
                      );
                      return t ? (t.name || t.label || String(t.id)) : null;
                    })
                    .filter(Boolean)}
                  onChange={(selectedKeys) => {
                    const standardKeys = roleTabNames.filter((name) =>
                      allowedStandardTabs.some(
                        (t) =>
                          String(name).toLowerCase() === String(t.name || '').toLowerCase() ||
                          String(name).toLowerCase() === String(t.label || '').toLowerCase()
                      )
                    );
                    setRoleTabNames([...standardKeys, ...selectedKeys]);
                  }}
                  lockedKeys={lockedCustomTabKeys}
                  placeholder="Select custom tabs..."
                />
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="search-section">
            <div className="search-box">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search objects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          {/* Permissions Table */}
          <div className="permissions-table-container">
            <table className="permissions-table">
              <thead>
                <tr>
                  <th className="object-column">Object</th>
                  <th className="checkbox-column">
                    <div className="header-checkbox">
                      <input
                        type="checkbox"
                        checked={areAllSelected('can_create')}
                        ref={(input) => {
                          if (input) {
                            input.indeterminate = areSomeSelected('can_create');
                          }
                        }}
                        onChange={(e) => handleSelectAll('can_create', e.target.checked)}
                        className="header-permission-checkbox"
                      />
                      <span>Create</span>
                    </div>
                  </th>
                  <th className="checkbox-column">
                    <div className="header-checkbox">
                      <input
                        type="checkbox"
                        checked={areAllSelected('can_read')}
                        ref={(input) => {
                          if (input) {
                            input.indeterminate = areSomeSelected('can_read');
                          }
                        }}
                        onChange={(e) => handleSelectAll('can_read', e.target.checked)}
                        className="header-permission-checkbox"
                      />
                      <span>Read</span>
                    </div>
                  </th>
                  <th className="checkbox-column">
                    <div className="header-checkbox">
                      <input
                        type="checkbox"
                        checked={areAllSelected('can_update')}
                        ref={(input) => {
                          if (input) {
                            input.indeterminate = areSomeSelected('can_update');
                          }
                        }}
                        onChange={(e) => handleSelectAll('can_update', e.target.checked)}
                        className="header-permission-checkbox"
                      />
                      <span>Update</span>
                    </div>
                  </th>
                  <th className="checkbox-column">
                    <div className="header-checkbox">
                      <input
                        type="checkbox"
                        checked={areAllSelected('can_delete')}
                        ref={(input) => {
                          if (input) {
                            input.indeterminate = areSomeSelected('can_delete');
                          }
                        }}
                        onChange={(e) => handleSelectAll('can_delete', e.target.checked)}
                        className="header-permission-checkbox"
                      />
                      <span>Delete</span>
                    </div>
                  </th>
                  <th className="checkbox-column">
                    <div className="header-checkbox">
                      <input
                        type="checkbox"
                        checked={areAllSelected('view_all')}
                        ref={(input) => {
                          if (input) {
                            input.indeterminate = areSomeSelected('view_all');
                          }
                        }}
                        onChange={(e) => handleSelectAll('view_all', e.target.checked)}
                        className="header-permission-checkbox"
                      />
                      <span>View All</span>
                    </div>
                  </th>
                  <th className="checkbox-column">
                    <div className="header-checkbox">
                      <input
                        type="checkbox"
                        checked={areAllSelected('modify_all')}
                        ref={(input) => {
                          if (input) {
                            input.indeterminate = areSomeSelected('modify_all');
                          }
                        }}
                        onChange={(e) => handleSelectAll('modify_all', e.target.checked)}
                        className="header-permission-checkbox"
                      />
                      <span>Modify All</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {standardPermissions.length > 0 && (
                  <>
                    <tr className="permission-section-header">
                      <td colSpan="7" className="section-header-cell">Standard Objects</td>
                    </tr>
                    {standardPermissions.map(renderBulkEditPermissionRow)}
                  </>
                )}
                {customPermissions.length > 0 && (
                  <>
                    <tr className="permission-section-header">
                      <td colSpan="7" className="section-header-cell">Custom Objects</td>
                    </tr>
                    {customPermissions.map(renderBulkEditPermissionRow)}
                  </>
                )}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div className="summary-section">
            <div className="summary-stats">
              <div className="stat-item">
                <span className="stat-number">{filteredPermissions.length}</span>
                <span className="stat-label">Total Objects</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">
                  {filteredPermissions.filter(p => p.is_existing).length}
                </span>
                <span className="stat-label">Existing Permissions</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">
                  {filteredPermissions.filter(p => !p.is_existing).length}
                </span>
                <span className="stat-label">New Permissions</span>
              </div>
              <div className="stat-item changed-stat">
                <span className="stat-number">
                  {filteredPermissions.filter(p => hasPermissionChanged(p)).length}
                </span>
                <span className="stat-label">Changed Permissions</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && permissions.length === 0 && (
        <div className="empty-state">
          <div className="permissions-tabs-section bulk-edit-tabs">
            <h3>Tabs</h3>
            <p className="permissions-tabs-summary text-muted small mb-2">
              Assign tabs this role can see. Standard tabs are limited by the role&apos;s license.
            </p>
            <div className="row g-3 justify-content-center mb-3">
              <div className="col-md-5">
                <label className="form-label small fw-semibold">Standard Tabs</label>
                <MultiSelectDropdown
                  picklistValues={allowedStandardTabs.map((t) => ({
                    key: t.name || t.label || String(t.id),
                    value: (t.label || t.name || t.id) + (t.object_name ? ` (${t.object_name})` : ''),
                  }))}
                  selectedValues={roleTabNames
                    .map((n) => {
                      const t = allowedStandardTabs.find(
                        (tab) =>
                          String(n).toLowerCase() === String(tab.name || '').toLowerCase() ||
                          String(n).toLowerCase() === String(tab.label || '').toLowerCase()
                      );
                      return t ? (t.name || t.label || String(t.id)) : null;
                    })
                    .filter(Boolean)}
                  onChange={(selectedKeys) => {
                    const customKeys = roleTabNames.filter((name) =>
                      allowedCustomTabs.some(
                        (t) =>
                          String(name).toLowerCase() === String(t.name || '').toLowerCase() ||
                          String(name).toLowerCase() === String(t.label || '').toLowerCase()
                      )
                    );
                    setRoleTabNames([...customKeys, ...selectedKeys]);
                  }}
                  lockedKeys={lockedStandardTabKeys}
                  placeholder="Select standard tabs..."
                />
              </div>
              <div className="col-md-5">
                <label className="form-label small fw-semibold">Custom Tabs</label>
                <MultiSelectDropdown
                  picklistValues={allowedCustomTabs.map((t) => ({
                    key: t.name || t.label || String(t.id),
                    value: (t.label || t.name || t.id) + (t.object_name ? ` (${t.object_name})` : ''),
                  }))}
                  selectedValues={roleTabNames
                    .map((n) => {
                      const t = allowedCustomTabs.find(
                        (tab) =>
                          String(n).toLowerCase() === String(tab.name || '').toLowerCase() ||
                          String(n).toLowerCase() === String(tab.label || '').toLowerCase()
                      );
                      return t ? (t.name || t.label || String(t.id)) : null;
                    })
                    .filter(Boolean)}
                  onChange={(selectedKeys) => {
                    const standardKeys = roleTabNames.filter((name) =>
                      allowedStandardTabs.some(
                        (t) =>
                          String(name).toLowerCase() === String(t.name || '').toLowerCase() ||
                          String(name).toLowerCase() === String(t.label || '').toLowerCase()
                      )
                    );
                    setRoleTabNames([...standardKeys, ...selectedKeys]);
                  }}
                  lockedKeys={lockedCustomTabKeys}
                  placeholder="Select custom tabs..."
                />
              </div>
            </div>
          </div>
          <i className="fas fa-shield-alt"></i>
          <h3>No object permissions found</h3>
          <p>No object permissions are available for this role. You can still assign tabs above and save.</p>
        </div>
      )}

      {/* Success Toast */}
      <AppToast
        show={showToast}
        message={toastMessage}
        variant={toastVariant}
        onClose={handleToastClose}
      />
    </div>
  );
};

export default PermissionBulkEdit;
